import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function CambridgePage() {
  return (
    <>
      <LocalBusinessSchema city="Cambridge" latitude={52.2053} longitude={0.1218} />
      <PageHero
        title="Fractional CISO Services in Cambridge"
        subtitle="Cybersecurity leadership for the UK's premier deep tech cluster"
        eyebrow="Cambridge"
        seoTitle="Fractional CISO Cambridge | The CISO Network"
        seoDescription="Expert fractional CISO services for Cambridge businesses. Access senior cybersecurity leadership for biotech, AI, and deep tech startups."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing the World's Leading Deep Tech Cluster
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Cambridge stands as Europe's most successful technology cluster, with a unique
                  ecosystem spanning biotechnology, artificial intelligence, semiconductor design,
                  and quantum computing. The city's "Silicon Fen" has produced more billion-dollar
                  companies than any other European city, creating an environment where cutting-edge
                  innovation is the norm.
                </p>
                <p>
                  Cambridge businesses face exceptionally demanding cybersecurity challenges.
                  Biotech and life sciences companies handle sensitive clinical trial data and
                  intellectual property that attracts sophisticated attackers. AI firms work with
                  valuable datasets and models requiring robust protection. Semiconductor and
                  quantum computing companies face nation-state level threats targeting their
                  breakthrough technologies.
                </p>
                <p>
                  Many Cambridge businesses are venture-backed startups or university spin-outs
                  serving global markets. These companies need world-class security leadership to
                  satisfy investors, win enterprise clients, and protect valuable IP - but often
                  can't justify a full-time CISO until later funding rounds. Our fractional CISO
                  service provides Cambridge businesses with the executive security expertise they
                  need to scale securely.
                </p>
                <p>
                  Cambridge produces some of the world's most valuable intellectual property, from
                  semiconductor designs to pharmaceutical research. The city's university spinouts,
                  biotech firms, and deep technology companies face sophisticated threats including
                  nation-state espionage and corporate IP theft. A fractional CISO with experience
                  protecting research-driven businesses is essential for companies operating at this
                  level of innovation.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique dynamics of Silicon Fen, from
                      university spin-outs to venture-backed scale-ups.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Silicon Fen Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Cambridge, the Science Park, and the wider
                      high-tech corridor.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Cambridge Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              World-class security leadership for world-changing technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Deep Tech Security',
                description:
                  'Protect valuable IP, AI models, and breakthrough technologies from sophisticated threats.',
              },
              {
                icon: Target,
                title: 'Investor Ready',
                description:
                  'Security programmes that satisfy due diligence and accelerate funding rounds.',
              },
              {
                icon: Users,
                title: 'Spin-Out Experience',
                description:
                  'CISOs who understand the unique journey from university lab to global business.',
              },
              {
                icon: Clock,
                title: 'Scale-Up Aligned',
                description:
                  'Flexible engagement that evolves with your funding stage and growth trajectory.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Cambridge - FAQs"
        subtitle="Common questions from Cambridge and Silicon Fen businesses"
        items={[
          {
            id: 'cambridge-1',
            question: 'How does a fractional CISO work with businesses in Cambridge?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Cambridge. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing biotech, deep tech, and university spinouts companies.',
          },
          {
            id: 'cambridge-2',
            question: 'What size of business benefits from a fractional CISO in Cambridge?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Cambridge startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'cambridge-3',
            question: 'What industries do your CISOs cover in Cambridge?',
            answer:
              "Our network includes CISOs with experience in biotech, deep tech, and university spinouts. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'cambridge-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'cambridge-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Cambridge Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your deep tech
            innovation.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
